// Custom styles for theme adaptive React Select
import Select, {
  ActionMeta,
  MultiValue,
  SingleValue,
  StylesConfig,
} from "react-select";

const getSelectStyles: (isDark: boolean) => StylesConfig<any, any> = (
  isDark,
) => ({
  control: (provided: any) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#f9fafb",
    borderColor: isDark ? "#374151" : "#d1d5db",
    boxShadow: "none",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  input: (provided: any) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: isDark ? "#374151" : "#e5e7eb",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    ":hover": {
      backgroundColor: isDark ? "#4b5563" : "#d1d5db",
      color: isDark ? "#fff" : "#111827",
    },
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : isDark
        ? "#1a202c"
        : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
});

// Theme detection for react-select
const isDark =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;
import { X } from "lucide-react";
import React from "react";

interface AddAssignmentModalProps {
  drivers: Driver[];
  onClose: () => void;
  onSubmit: (data: AssignmentForm) => Promise<void> | void;
  open: boolean;
  subzones: Subzone[];
  vehicles: Vehicle[];
  zones: ZoneOption[];
}

interface AssignmentForm {
  availability: boolean;
  days: string[];
  driverId: string;
  notes: string;
  subzones: string[];
  vehicleId: string;
  zones: string[];
}
interface Driver {
  id: string;
  name: string;
}

interface Subzone {
  id: string;
  name: string;
  zoneId: string;
}

interface Vehicle {
  id: string;
  registration: string;
  type: string; // Car, Bike, Van, etc.
}

interface ZoneOption {
  id: string;
  name: string;
}

const daysOfWeek: string[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const AddCourierModal: React.FC<AddAssignmentModalProps> = ({
  drivers,
  onClose,
  onSubmit,
  open,
  subzones,
  vehicles,
  zones,
}) => {
  const [form, setForm] = React.useState<AssignmentForm>({
    availability: true,
    days: [],
    driverId: drivers[0]?.id || "",
    notes: "",
    subzones: [],
    vehicleId: vehicles[0]?.id || "",
    zones: zones.length > 0 ? [zones[0].id] : [],
  });
  // Checkbox state for enabling subzone selection
  const [enableSubzones, setEnableSubzones] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({
        availability: true,
        days: [],
        driverId: drivers[0]?.id || "",
        notes: "",
        subzones: [],
        vehicleId: vehicles[0]?.id || "",
        zones: zones.length > 0 ? [zones[0].id] : [],
      });
      setEnableSubzones(false);
      setSaving(false);
    }
  }, [open, drivers, vehicles, zones]);

  if (!open) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (type === "checkbox" && name.startsWith("day-")) {
      const day = name.split("-")[1];
      setForm((f: AssignmentForm) => ({
        ...f,
        days: checked
          ? [...f.days, day]
          : f.days.filter((d: string) => d !== day),
      }));
    } else if (type === "checkbox") {
      setForm((f: AssignmentForm) => ({ ...f, [name]: checked }));
    } else if (name === "zones") {
      // Multi-select for zones
      const selected = Array.from(
        (e.target as HTMLSelectElement).selectedOptions,
      ).map((opt) => opt.value);
      setForm((f: AssignmentForm) => ({ ...f, zones: selected }));
    } else {
      setForm((f: AssignmentForm) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !form.driverId ||
      !form.vehicleId ||
      !form.zones.length ||
      form.days.length === 0
    ) {
      alert(
        "Please fill all required fields and select at least one day and one zone.",
      );
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <form
          className="w-full rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900"
          onSubmit={handleSubmit}
        >
          <button
            aria-label="Close"
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200"
            onClick={onClose}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-gray-100">
            Assign Driver
          </h2>

          {/* Driver (searchable select) */}
          <div className="relative mb-4">
            <Select<{ label: string; value: string }, false>
              classNamePrefix="react-select"
              isSearchable
              name="driverId"
              onChange={(selected) => {
                setForm((f) => ({
                  ...f,
                  driverId: selected ? selected.value : "",
                }));
              }}
              options={drivers.map((d) => ({ label: d.name, value: d.id }))}
              placeholder="Select driver..."
              styles={getSelectStyles(isDark)}
              value={
                drivers
                  .filter((d) => d.id === form.driverId)
                  .map((d) => ({ label: d.name, value: d.id }))[0] || null
              }
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Driver
            </label>
          </div>

          {/* Vehicle (searchable select) */}
          <div className="relative mb-4">
            <Select<{ label: string; value: string }, false>
              classNamePrefix="react-select"
              isSearchable
              name="vehicleId"
              onChange={(selected) => {
                setForm((f) => ({
                  ...f,
                  vehicleId: selected ? selected.value : "",
                }));
              }}
              options={vehicles.map((v) => ({
                label: `${v.type} - ${v.registration}`,
                value: v.id,
              }))}
              placeholder="Select vehicle..."
              styles={getSelectStyles(isDark)}
              value={
                vehicles
                  .filter((v) => v.id === form.vehicleId)
                  .map((v) => ({
                    label: `${v.type} - ${v.registration}`,
                    value: v.id,
                  }))[0] || null
              }
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Vehicle
            </label>
          </div>

          {/* Zones (multi-select, searchable, theme adaptive) */}
          <div className="relative mb-4">
            <Select
              classNamePrefix="react-select"
              isMulti
              isSearchable
              name="zones"
              onChange={(selected) => {
                setForm((f) => ({
                  ...f,
                  zones: selected ? selected.map((s) => s.value) : [],
                }));
              }}
              options={zones.map((z) => ({ label: z.name, value: z.id }))}
              placeholder="Select zones..."
              styles={getSelectStyles(isDark)}
              value={form.zones.map((zId) => {
                const found = zones.find(
                  (zone) => zone.id?.toString() === zId?.toString(),
                );
                return found
                  ? { label: found.name, value: found.id }
                  : { label: zId, value: zId };
              })}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Zones
            </label>
          </div>

          {/* Subzones: Only show if at least one zone is selected and there are subzones for those zones */}
          {form.zones.length > 0 &&
            subzones.some((sz) =>
              form.zones.some(
                (zId) => zId?.toString() === sz.zoneId?.toString(),
              ),
            ) && (
              <div className="relative mb-4">
                <label className="mb-2 flex items-center gap-2 text-gray-700 dark:text-gray-200">
                  <input
                    checked={enableSubzones}
                    className="accent-blue-600 dark:accent-blue-400"
                    onChange={(e) => {
                      setEnableSubzones(e.target.checked);
                      if (!e.target.checked) {
                        setForm((f) => ({ ...f, subzones: [] }));
                      }
                    }}
                    type="checkbox"
                  />
                  Assign specific subzones (optional)
                </label>
                {enableSubzones && (
                  <>
                    <Select
                      classNamePrefix="react-select"
                      isMulti
                      isSearchable
                      name="subzones"
                      onChange={(
                        selected: MultiValue<{ label: string; value: string }>,
                        _actionMeta: ActionMeta<{
                          label: string;
                          value: string;
                        }>,
                      ) => {
                        setForm((f) => ({
                          ...f,
                          subzones: selected
                            ? selected.map((s) => s.value)
                            : [],
                        }));
                      }}
                      options={subzones
                        .filter((sz: Subzone) =>
                          form.zones.some(
                            (zId) => zId?.toString() === sz.zoneId?.toString(),
                          ),
                        )
                        .map((sz: Subzone) => ({
                          label: sz.name,
                          value: sz.id,
                        }))}
                      placeholder="Select subzones..."
                      styles={getSelectStyles(isDark)}
                      value={form.subzones.map((szId: string) => {
                        const found = subzones.find(
                          (s: Subzone) => s.id?.toString() === szId?.toString(),
                        );
                        return found
                          ? { label: found.name, value: found.id }
                          : { label: szId, value: szId };
                      })}
                    />
                    <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                      Subzones
                    </label>
                  </>
                )}
              </div>
            )}

          {/* Days */}
          <div className="mb-4">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Days
            </span>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map((day: string) => (
                <label
                  className="flex items-center gap-1 text-gray-700 dark:text-gray-200"
                  key={day}
                >
                  <input
                    checked={form.days.includes(day)}
                    className="accent-blue-600 dark:accent-blue-400"
                    name={`day-${day}`}
                    onChange={handleChange}
                    type="checkbox"
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>

          {/* Availability */}
          <label className="mb-4 flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <input
              checked={form.availability}
              className="accent-blue-600 dark:accent-blue-400"
              name="availability"
              onChange={handleChange}
              type="checkbox"
            />
            Available
          </label>

          {/* Notes */}
          <div className="relative mb-4">
            <textarea
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="notes"
              onChange={handleChange}
              placeholder="Notes / Instructions"
              value={form.notes}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Notes / Instructions
            </label>
          </div>

          {/* Buttons */}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              className="rounded bg-gray-300 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-60 dark:bg-blue-500"
              disabled={saving}
              type="submit"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      fill="currentColor"
                    />
                  </svg>
                  Assigning...
                </span>
              ) : (
                "Assign"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default AddCourierModal;
